import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const SoftphoneContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Statuses:
//   unavailable  - agent not on call duty; widget hidden
//   registering  - fetching token + connecting to AT WebRTC
//   ready        - registered; can place/receive calls
//   calling      - outbound dialing or inbound ringing
//   connected    - call answered and media flowing
//   disconnected - SDK dropped; auto-reconnect in progress

let _atSdk = null;
async function loadATSdk() {
  if (_atSdk) return _atSdk;
  const mod = await import('africastalking-client');
  _atSdk = mod.default ?? mod;
  return _atSdk;
}

async function fetchWebRTCToken(authToken) {
  const res = await fetch(`${API_URL}/api/agent/webrtc-token`, {
    credentials: 'include',
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to fetch WebRTC token');
  }
  return res.json();
}

// Report the softphone's actual registration state to the backend. This is
// what makes inbound emergency calls route to this browser (registered=true)
// or fall back to the agent's mobile / skip them (registered=false). The
// clientName is the exact (unique per registration) identity AT knows this
// client by -- routing dials it, so it must be sent on every registered=true.
// Fire and forget: a failed report just means the timestamp ages out on its own.
function reportWebRTCPresence(authToken, registered, clientName) {
  return fetch(`${API_URL}/api/users/me/webrtc-presence`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({ registered, client_name: clientName || null }),
  }).catch(() => {});
}

// Report to the backend that an inbound call rang this agent for the full
// no-answer window without being answered. This is what credits a per-agent
// "missed" on the Team drill-down and (via the auto-decline that fires
// alongside it) lets round-robin routing hop to the next agent. Fire and
// forget: a dropped report just means that one miss isn't counted.
function reportCallMissed(authToken, callerNumber) {
  return fetch(`${API_URL}/api/agent/call-missed`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({ caller_number: callerNumber || null }),
  }).catch(() => {});
}

// Re-affirm registration well inside the backend's staleness window
// (WEBRTC_PRESENCE_STALE_SECONDS, default 120s) so a live softphone is never
// mistaken for a dead one.
const PRESENCE_HEARTBEAT_MS = 45_000;

// How long an inbound call rings one agent before the softphone auto-declines
// it so routing moves on to the next agent in the round-robin roster. Africa's
// Talking has no per-<Dial> ring timeout, so this client-side cutoff is what
// enforces the 10s hand-off (and marks the ring-out as a miss for this agent).
const UNANSWERED_RING_MS = 10_000;

export function SoftphoneProvider({ children }) {
  const { user, token, isCommandCenter, isAdmin } = useAuth();
  const [status, setStatus] = useState('unavailable');
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);

  // Use a generation counter so stale reconnect callbacks don't overwrite
  // state that belongs to a newer registration attempt.
  const generationRef = useRef(0);
  const clientRef = useRef(null);
  const callTimerRef = useRef(null);
  const missTimerRef = useRef(null);
  // Effective unanswered-ring window (ms). Refreshed from the webrtc-token
  // response on every registration so an admin change to the platform setting
  // takes effect the next time the softphone (re)registers. Seeded with the
  // hardcoded default until the first token load.
  const ringMsRef = useRef(UNANSWERED_RING_MS);
  const reconnectTimerRef = useRef(null);
  const presenceTimerRef = useRef(null);
  const retryDelayRef = useRef(3000);
  // Synchronous re-entrancy guard for register() (see register()).
  const registeringRef = useRef(false);

  const stopPresenceHeartbeat = useCallback(() => {
    if (presenceTimerRef.current) {
      clearInterval(presenceTimerRef.current);
      presenceTimerRef.current = null;
    }
  }, []);

  const stopCallTimer = useCallback(() => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
  }, []);

  // Cancel the pending unanswered-ring auto-decline. Called the moment the ring
  // resolves any other way -- this agent answers or rejects, or another agent
  // grabs the call and AT tears down this leg (hangup) -- so a call that didn't
  // actually ring out for the full window is never mis-counted as a miss.
  const clearMissTimer = useCallback(() => {
    if (missTimerRef.current) {
      clearTimeout(missTimerRef.current);
      missTimerRef.current = null;
    }
  }, []);

  const cleanupClient = useCallback(() => {
    // Increment generation so any in-flight callbacks from the old client are
    // ignored when they fire after we've moved on to a new registration.
    generationRef.current += 1;
    if (clientRef.current) {
      // logout() sends the AT `unregister` request so this clientName's
      // registration is torn down. Without it, every reconnect/re-register
      // leaves a GHOST registration of the same clientName alive at AT, and AT
      // then routes inbound `<Dial>` invites to a stale ghost -- the live tab
      // never gets `incomingcall`, so only the SSE toast shows. hangup() alone
      // (what we did before) ends any active call but does NOT unregister.
      try { clientRef.current.hangup(); } catch {}
      try { clientRef.current.logout(); } catch {}
      clientRef.current = null;
    }
    clearMissTimer();
    stopCallTimer();
    stopPresenceHeartbeat();
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    // Tell the backend we're no longer reachable in the browser, so inbound
    // routing stops targeting our clientName immediately.
    reportWebRTCPresence(token, false);
  }, [clearMissTimer, stopCallTimer, stopPresenceHeartbeat, token]);

  const register = useCallback(async () => {
    // Don't stack registrations. A ref (not React state) is the real guard:
    // status is async, so two register() calls in the same tick would both
    // pass a `status === 'registering'` check and create two AT Clients (two
    // registrations of the same clientName -> AT routes inbound to the wrong
    // one). The ref flips synchronously.
    if (registeringRef.current) return;
    registeringRef.current = true;
    setStatus('registering');

    const generation = generationRef.current + 1;
    generationRef.current = generation;

    try {
      const AT = await loadATSdk();
      const tokenData = await fetchWebRTCToken(token);
      // The unique clientName AT minted this token for. Routing must dial this
      // exact name, so we report it back to the backend on `ready`.
      const clientName = tokenData.client_name;
      // Pick up the deployment's configured ring window (admin-settable). Guard
      // against a missing/garbage value so the timer can never be 0 or NaN.
      const ringSeconds = Number(tokenData.unanswered_ring_seconds);
      if (Number.isFinite(ringSeconds) && ringSeconds > 0) {
        ringMsRef.current = ringSeconds * 1000;
      }

      // Another registration started while we were awaiting - bail.
      if (generation !== generationRef.current) return;

      cleanupClient();
      generationRef.current = generation; // cleanupClient increments; reset

      const client = new AT.Client(tokenData.token);
      clientRef.current = client;

      client.on('ready', () => {
        if (generation !== generationRef.current) return;
        registeringRef.current = false; // registration finished
        setStatus('ready');
        retryDelayRef.current = 3000; // reset backoff on successful connect
        // We are genuinely registered with AT now -- announce it (with the exact
        // clientName) and keep re-affirming so the backend keeps routing here.
        reportWebRTCPresence(token, true, clientName);
        stopPresenceHeartbeat();
        presenceTimerRef.current = setInterval(() => {
          reportWebRTCPresence(token, true, clientName);
        }, PRESENCE_HEARTBEAT_MS);
      });

      client.on('notready', () => {
        if (generation !== generationRef.current) return;
        setStatus('disconnected');
        stopPresenceHeartbeat();
        reportWebRTCPresence(token, false);
      });

      // AT WebRTC has a documented tendency to drop and not self-recover.
      // We re-mint a fresh token and re-register on every offline event.
      client.on('offline', () => {
        if (generation !== generationRef.current) return;
        registeringRef.current = false; // allow the reconnect below to re-register
        setStatus('disconnected');
        stopPresenceHeartbeat();
        reportWebRTCPresence(token, false);
        const delay = retryDelayRef.current;
        retryDelayRef.current = Math.min(delay * 2, 60000);
        reconnectTimerRef.current = setTimeout(() => register(), delay);
      });

      client.on('incomingcall', (params) => {
        if (generation !== generationRef.current) return;
        setStatus('calling');
        const caller = params.from || 'Unknown';
        // AT has no per-<Dial> ring timeout, so enforce the hand-off here: if the
        // agent hasn't answered within the window, auto-decline (which ends this
        // leg so routing rings the next agent) and report the ring-out as a miss.
        clearMissTimer();
        missTimerRef.current = setTimeout(() => {
          missTimerRef.current = null;
          try { client.hangup(); } catch {}
          setIncomingCall(null);
          setStatus('ready');
          reportCallMissed(token, params.from || null);
        }, ringMsRef.current);
        setIncomingCall({
          from: caller,
          answer: () => {
            clearMissTimer();
            try { client.answer(); } catch {}
            setIncomingCall(null);
          },
          reject: () => {
            // Manual decline is a deliberate choice, not a ring-out, so it is
            // NOT counted as a miss -- only the timeout above is.
            clearMissTimer();
            try { client.hangup(); } catch {}
            setIncomingCall(null);
            setStatus('ready');
          },
        });
      });

      client.on('calling', (params) => {
        if (generation !== generationRef.current) return;
        setStatus('calling');
        setActiveCall(prev => prev ? prev : {
          to: params.phoneNumber || params.clientName || '',
          label: '',
          direction: 'outbound',
          duration: 0,
        });
      });

      client.on('callaccepted', () => {
        if (generation !== generationRef.current) return;
        setStatus('connected');
        setActiveCall(prev => prev ? { ...prev, duration: 0 } : { to: '', label: '', direction: 'inbound', duration: 0 });
        let secs = 0;
        callTimerRef.current = setInterval(() => {
          secs += 1;
          setActiveCall(prev => (prev ? { ...prev, duration: secs } : null));
        }, 1000);
      });

      client.on('hangup', (cause) => {
        if (generation !== generationRef.current) return;
        // Another agent may have answered first, tearing down this ring leg
        // before the window elapsed -- cancel the pending miss so we don't
        // charge this agent for a call they never got a full chance to take.
        clearMissTimer();
        stopCallTimer();
        setIncomingCall(null);
        setActiveCall(null);
        setStatus('ready');
        if (cause?.code && cause.code !== 'normalClearing' && cause.reason) {
          toast.info(`Call ended: ${cause.reason}`);
        }
      });

    } catch (err) {
      registeringRef.current = false; // failed; allow retry
      if (generation !== generationRef.current) return;
      console.error('Softphone registration failed:', err);
      setStatus('disconnected');
      const delay = retryDelayRef.current;
      retryDelayRef.current = Math.min(delay * 2, 60000);
      reconnectTimerRef.current = setTimeout(() => register(), delay);
    }
  // register intentionally omits itself from deps to avoid infinite loops;
  // it's stable because all state it captures is via refs or closures.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, cleanupClient, clearMissTimer, stopCallTimer, stopPresenceHeartbeat]);

  const unregister = useCallback(() => {
    registeringRef.current = false;
    cleanupClient();
    setStatus('unavailable');
    setIncomingCall(null);
    setActiveCall(null);
  }, [cleanupClient]);

  // Who registers a softphone:
  //  - agents/admins: only while ON CALL DUTY (they explicitly go online).
  //  - responders: whenever logged in, so agents can reach them in-house without
  //    the responder having to toggle anything (they may not be settled at a desk).
  useEffect(() => {
    if (!user) return;
    const isResponder = ['FIRST_RESPONDER', 'SECOND_RESPONDER'].includes(user.role);
    const eligible = ((isCommandCenter || isAdmin) && user.call_available) || isResponder;
    if (eligible) {
      if (status === 'unavailable') register();
    } else {
      if (status !== 'unavailable') unregister();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.call_available, user?.role, isCommandCenter, isAdmin]);

  useEffect(() => () => cleanupClient(), [cleanupClient]);

  const call = useCallback((number, label = '') => {
    if (!clientRef.current || (status !== 'ready')) {
      toast.error('Softphone not connected — go on call duty first.');
      return;
    }
    setActiveCall({ to: number, label: label || number, direction: 'outbound', duration: 0 });
    setStatus('calling');
    try {
      clientRef.current.call(number);
    } catch (err) {
      toast.error(`Call failed: ${err.message}`);
      setActiveCall(null);
      setStatus('ready');
    }
  }, [status]);

  const hangup = useCallback(() => {
    stopCallTimer();
    if (clientRef.current) {
      try { clientRef.current.hangup(); } catch {}
    }
    setIncomingCall(null);
    setActiveCall(null);
    setStatus(clientRef.current ? 'ready' : 'unavailable');
  }, [stopCallTimer]);

  const mute = useCallback((shouldMute) => {
    if (!clientRef.current) return;
    try {
      // AT SDK method names are muteAudio/unmuteAudio (not mute/unmute).
      if (shouldMute) clientRef.current.muteAudio();
      else clientRef.current.unmuteAudio();
    } catch {}
  }, []);

  return (
    <SoftphoneContext.Provider value={{ status, incomingCall, activeCall, call, hangup, mute, register, unregister }}>
      {children}
    </SoftphoneContext.Provider>
  );
}

export function useSoftphone() {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) throw new Error('useSoftphone must be used inside SoftphoneProvider');
  return ctx;
}
